import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';
import { create } from 'lodash';

import { CONFIG } from 'src/config-global';
import { paths } from 'src/routes/paths';
import { mutate } from 'swr';

// ----------------------------------------------------------------------

export const axiosInstance = axios.create({
  baseURL: CONFIG.site.serverUrl,
  headers: {
    custom: CONFIG.api.customHeader,
  },
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) =>
    Promise.reject(
      (error.response && error.response.data) || 'Something went wrong!',
    ),
);

export default axiosInstance;
// ----------------------------------------------------------------------
export function getServerError<T>(
  error: {
    message: string;
    errors: { [P in keyof T]?: string };
  },
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type, @typescript-eslint/ban-types
  setError: Function,
) {
  const keys = Object.keys(error.errors);
  if (keys.length > 0) {
    keys.forEach((key) => {
      setError(key as keyof T, {
        type: 'server',
        message: error.errors[key as keyof T] ?? '',
      });
    });
  }
  return error.message;
}

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, {
      ...config,
    });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const webAxiosInstance = axios.create({
  baseURL: CONFIG.site.serverUrl,
  headers: {
    custom: CONFIG.api.customHeader,
  },
});

webAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle errors
    if (error.response && error.response.status === 401) {
      // Redirect to the login page on 401 errors with returnTo parameter
      const returnTo = window.location.pathname + window.location.search;
      const loginUrl = `${paths.userAuth.signIn}?returnTo=${encodeURIComponent(returnTo)}`;
      window.location.href = loginUrl;
    }
    return Promise.reject(
      (error.response && error.response.data) || 'Something went wrong!',
    );
  },
);

// ----------------------------------------------------------------------

export const webFetcher = async (
  args: string | [string, AxiosRequestConfig],
) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await webAxiosInstance.get(url, {
      ...config,
    });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export function reValidate(endpoint: any) {
  mutate(
    (key: any) => (
      (typeof key === 'string' && key.includes(endpoint)) ||
        // eslint-disable-next-line no-sequences
        (Array.isArray(key) && key[0].includes(endpoint)),
      undefined,
      { revalidate: true }
    ),
  );
}

// ----------------------------------------------------------------------
export function addServerErrors<T>(
  errors: { [P in keyof T]?: string },
  setError: (
    fieldName: keyof T,
    error: { type: string; message: string },
  ) => void,
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T] ?? '',
    });
  });
}

// ----------------------------------------------------------------------

export const endpoints = {
  web: {
    auth: {
      sendOtp: '/api/v1/web/auth/send-otp',
      verifyOtp: '/api/v1/web/auth/verify-otp',
      completeProfile: '/api/v1/web/auth/complete-profile',
      me: '/api/v1/web/auth/me',
    },
    locations: {
      key: '/api/v1/web/locations',
      list: '/api/v1/web/locations',
      details: (locationId: string) => `/api/v1/web/locations/${locationId}`,
      spaces: (locationId: number) =>
        `/api/v1/web/locations/${locationId}/spaces`,
    },
    experiences: {
      list: '/api/v1/web/experiences',
      details: (experienceId: number) =>
        `/api/v1/web/experiences/${experienceId}`,
      slots: (experienceId: number) =>
        `/api/v1/web/experiences/${experienceId}/slots`,
    },
    bookings: {
      book: '/api/v1/web/bookings',
      cancelBooking: (bookingId: number) =>
        `/api/v1/web/bookings/${bookingId}/cancel`,
    },
    classes: {
      sessions: {
        list: '/api/v1/web/classes/sessions',
        related: (sessionId: number) =>
          `/api/v1/web/classes/sessions/${sessionId}/related`,
        tickets: (sessionId: number) =>
          `/api/v1/web/classes/sessions/${sessionId}/tickets`,
        details: (sessionId: number) =>
          `/api/v1/web/classes/sessions/${sessionId}`,
        book: (sessionId: number) =>
          `/api/v1/web/classes/sessions/${sessionId}/book`,
      },
    },
    trips: {
      list: '/api/v1/web/trips',
      details: (tripId: number) => `/api/v1/web/trips/${tripId}`,
      book: (tripId: number) => `/api/v1/web/trips/${tripId}/book`,
    },
    users: {
      search: '/api/v1/web/users/search',
      findByPhoneNumber: '/api/v1/web/users/find-one',
    },
  },
  tenants: {
    key: 'tenants',
    me: '/api/v1/tenants/me',
    modules: '/api/v1/tenants/me/modules',
    settings: '/api/v1/tenants/me/settings',
    paymentMethods: '/api/v1/tenants/me/payment-methods',
    addMedia: '/api/v1/tenants/me/media',
    deleteMedia: (tenantMediaId: number) =>
      `/api/v1/tenants/me/media/${tenantMediaId}`,
    contacts: {
      key: 'tenants.contacts',
      me: '/api/v1/tenants/me/contact-numbers',
      deleteContact: (tenantContactNumberId: number) =>
        `/api/v1/tenants/me/contact-numbers/${tenantContactNumberId}`,
      updateContact: (tenantContactNumberId: number) =>
        `/api/v1/tenants/me/contact-numbers/${tenantContactNumberId}`,
    },
  },
  core: {
    amenities: '/api/v1/core/amenities',
    cities: '/api/v1/core/cities',
    zones: (cityId: string) => `/api/v1/core/cities/${cityId}`,
    tags: '/api/v1/core/tags',
    categories: '/api/v1/core/categories',
    currencies: '/api/v1/core/currencies',
    sources: '/api/v1/core/sources',
  },
  file: {
    upload: '/api/v1/files/upload',
    uploadFiles: '/api/v1/files/upload-files',
  },
  locations: {
    key: 'locations',
    add: '/api/v1/locations',
    spaces: (locationId: number) => `/api/v1/locations/${locationId}/spaces`,
    edit: (locationId: string) => `/api/v1/locations/${locationId}`,
    delete: (locationId: string) => `/api/v1/locations/${locationId}`,
    list: '/api/v1/locations',
    details: (locationId: string) => `/api/v1/locations/${locationId}`,
    toggleStatus: (locationId: string) =>
      `/api/v1/locations/${locationId}/toggle-status`,
    short: '/api/v1/locations/list',
    reorder: '/api/v1/locations/reorder',
    space: {
      reorder: (locationId: number) =>
        `/api/v1/locations/${locationId}/spaces/reorder`,
    },
  },
  banners: {
    key: '/banners',
    add: '/api/v1/banners',
    getAll: '/api/v1/banners',
    list: '/api/v1/banners',
    get: (id: number) => `/api/v1/banners/${id}`,
    update: (id: number) => `/api/v1/banners/${id}`,
    delete: (id: number) => `/api/v1/banners/${id}`,
    toggleStatus: (id: number) => `/api/v1/banners/${id}/toggle-status`,
    reorder: '/api/v1/banners/reorder',
  },
  addons: {
    key: '/addons',
    list: '/api/v1/addons',
    add: '/api/v1/addons',
    get: (id: number) => `/api/v1/addons/${id}`,
    update: (id: number) => `/api/v1/addons/${id}`,
    toggleStatus: (id: number) => `/api/v1/addons/${id}/toggle-status`,
    offerings: {
      list: (locationId: number) =>
        `/api/v1/locations/${locationId}/addon-offerings`,
      add: (locationId: number) =>
        `/api/v1/locations/${locationId}/addon-offerings`,
      get: (locationId: number, id: number) =>
        `/api/v1/locations/${locationId}/addon-offerings/${id}`,
      update: (locationId: number, id: number) =>
        `/api/v1/locations/${locationId}/addon-offerings/${id}`,
      toggleStatus: (locationId: number, id: number) =>
        `/api/v1/locations/${locationId}/addon-offerings/${id}/toggle-status`,
      reorder: (locationId: number) =>
        `/api/v1/locations/${locationId}/addon-offerings/reorder`,
      assignSpaces: (locationId: number, id: number) =>
        `/api/v1/locations/${locationId}/addon-offerings/${id}/spaces`,
      removeSpaces: (locationId: number, id: number) =>
        `/api/v1/locations/${locationId}/addon-offerings/${id}/spaces`,
    },
  },
  spaces: {
    key: 'spaces',
    add: '/api/v1/spaces',
    edit: (spaceId: string) => `/api/v1/spaces/${spaceId}`,
    list: '/api/v1/spaces',
    details: (spaceId: string) => `/api/v1/spaces/${spaceId}`,
    delete: (spaceId: string) => `/api/v1/spaces/${spaceId}`,
    toggleStatus: (spaceId: string) =>
      `/api/v1/spaces/${spaceId}/toggle-status`,
    addAvailability: (spaceId: string) =>
      `/api/v1/spaces/${spaceId}/availability-rules`,
    addMedia: (spaceId: number) => `/api/v1/spaces/${spaceId}/media`,
    deleteMedia: (spaceId: number, spaceMediaId: number) =>
      `/api/v1/spaces/${spaceId}/media/${spaceMediaId}`,
    deleteAvailability: (spaceId: string, ruleId: string) =>
      `/api/v1/spaces/${spaceId}/availability-rules/${ruleId}`,
    getSpacePricing: (spaceId: number) =>
      `/api/v1/spaces/${spaceId}/price-rules`,
    addPricing: (spaceId: number) => `/api/v1/spaces/${spaceId}/price-rules`,
    updatePricing: (spaceId: number, priceRuleId: number) =>
      `/api/v1/spaces/${spaceId}/price-rules/${priceRuleId}`,
    reorderPricingRules: (spaceId: number) =>
      `/api/v1/spaces/${spaceId}/price-rules/reorder`,
    deletePricing: (spaceId: string, ruleId: string) =>
      `/api/v1/spaces/${spaceId}/price-rules/${ruleId}`,
    types: '/api/v1/core/space-types',
    tickets: {
      add: (spaceId: number) => `/api/v1/spaces/${spaceId}/tickets`,
      list: (spaceId: number) => `/api/v1/spaces/${spaceId}/tickets`,
      listSort: (spaceId: number) => `/api/v1/spaces/${spaceId}/tickets/list`,
      details: (spaceId: number, ticketId: number) =>
        `/api/v1/spaces/${spaceId}/tickets/${ticketId}`,
      edit: (spaceId: number, ticketId: number) =>
        `/api/v1/spaces/${spaceId}/tickets/${ticketId}`,
      delete: (spaceId: number, ticketId: number) =>
        `/api/v1/spaces/${spaceId}/tickets/${ticketId}`,
      toggleStatus: (spaceId: number, ticketId: number) =>
        `/api/v1/spaces/${spaceId}/tickets/${ticketId}/toggle-status`,
      reorder: (spaceId: number) => `/api/v1/spaces/${spaceId}/tickets/reorder`,
    },
    slots: (spaceId: number) => `/api/v1/spaces/${spaceId}/slots`,
  },

  auth: {
    me: '/api/v1/auth/me',
    login: '/api/v1/auth/login',
    logout: '/api/v1/auth/logout',
    updateFcmToken: '/api/v1/auth/update-fcm-token',
    resetPassword: '/api/v1/auth/reset-password',
    forgetPassword: '/api/v1/auth/forget-password',
    changePassword: '/api/v1/auth/change-password',
  },
  notifications: {
    list: '/api/v1/notifications',
    markAllAsRead: '/api/v1/notifications/mark-all-as-read',
    reValidateKey: 'notifications',
  },
  bookings: {
    reValidateKey: 'bookings',
    list: '/api/v1/bookings',
    details: (id: string) => `/api/v1/bookings/${id}`,
    calender: '/api/v1/bookings/calender',
    create: '/api/v1/bookings',
    createWithSlots: '/api/v1/bookings/slots',
    update: (id: string) => `/api/v1/bookings/${id}`,
    updateWithSlots: (id: string) => `/api/v1/bookings/${id}/slots`,
    complete: (id: string) => `/api/v2/bookings/${id}/complete`,
    completeBulk: '/api/v2/bookings/complete-bulk',
    contact: (id: string) => `/api/v1/bookings/${id}/mark-as-contacted`,
    miss: (id: string) => `/api/v1/bookings/${id}/mark-as-missed`,
    cancel: (id: string) => `/api/v1/bookings/${id}/cancel`,
    confirmCompleted: (id: string) =>
      `/api/v1/bookings/${id}/confirm-completed`,
    createRecurring: '/api/v1/bookings/recurrences',
    previewRecurring: '/api/v1/bookings/recurrences/preview',
    import: '/api/v1/bookings/import',
    export: '/api/v1/bookings/export',
    delete: (id: number) => `/api/v1/bookings/${id}`,
    deleteRecurring: (id: number) => `/api/v1/bookings/recurrences/${id}`,
    statistics: '/api/v1/bookings/statistics',
    userStatistics: '/api/v1/bookings/user-statistics',
    userBookingSummary: '/api/v1/bookings/user-booking-summary',
    addTransaction: (id: string) => `/api/v1/bookings/${id}/transactions`,
  },
  auditLogs: {
    list: '/api/v1/audit-trails',
  },
  profile: {
    update: '/api/v1/admins/me',
  },
  class: {
    key: 'classes',
    sessions: '/api/v1/classes/sessions/many',
    sessionsList: '/api/v1/classes/sessions',
    createSessionBooking: (eventId: number, sessionId: number) =>
      `/api/v1/classes/${eventId}/sessions/${sessionId}/bookings`,
    sessionBookings: `/api/v1/classes/sessions/bookings`,
    updateSessionBooking: (bookingId: number) =>
      `/api/v1/classes/sessions/bookings/${bookingId}`,
    moveSessionBooking: (bookingId: number) =>
      `/api/v1/classes/sessions/bookings/${bookingId}/move`,
    add: '/api/v1/classes',
    duplicate: (id: number) => `/api/v1/classes/${id}/duplicate`,
    duplicateRange: '/api/v1/classes/duplicate-range',
    edit: (id: number) => `/api/v1/classes/${id}`,
    editSession: (id: number, sessionId: number) =>
      `/api/v1/classes/${id}/sessions/${sessionId}`,
    details: (classId: number) => `/api/v1/classes/${classId}`,
    sessionDetails: (classId: number, sessionId: number) =>
      `/api/v1/classes/${classId}/sessions/${sessionId}`,
    relatedSessions: (classId: number, sessionId: number) =>
      `/api/v1/classes/${classId}/sessions/${sessionId}/related`,
    deleteRecurrence: (classId: number, recurrenceId: number) =>
      `/api/v1/classes/${classId}/recurrences/${recurrenceId}`,
    deleteOccurrence: (classId: number, sessionId: number) =>
      `/api/v1/classes/${classId}/sessions/${sessionId}`,
    userCourses: (userId: number) => `/api/v1/classes/users/${userId}/courses`,
    courseParticipants: (eventId: number) =>
      `/api/v1/classes/${eventId}/participants`,
    userCourseBookings: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}/bookings`,
    userCourseTransactions: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}/transactions`,
    completeEnrollment: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}/complete`,
    cancelEnrollment: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}/cancel`,
    deleteEnrollment: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}`,
    addEnrollmentPayment: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}/payments`,
    confirmCompletedEnrollment: (eventId: number, userId: number) =>
      `/api/v1/classes/${eventId}/participants/${userId}/confirm-completed`,
    media: {
      delete: (eventId: number, mediaId: number) =>
        `/api/v1/classes/${eventId}/media/${mediaId}`,
      edit: (eventId: number) => `/api/v1/classes/${eventId}/media`,
    },
    tickets: {
      add: (eventId: number) => `/api/v1/classes/${eventId}/tickets`,
      list: (eventId: number) => `/api/v1/classes/${eventId}/tickets`,
      listSort: (eventId: number) => `/api/v1/classes/${eventId}/tickets/list`,
      details: (eventId: number, ticketId: number) =>
        `/api/v1/classes/${eventId}/tickets/${ticketId}`,
      edit: (eventId: number, ticketId: number) =>
        `/api/v1/classes/${eventId}/tickets/${ticketId}`,
      delete: (eventId: number, ticketId: number) =>
        `/api/v1/classes/${eventId}/tickets/${ticketId}`,
      toggleStatus: (eventId: number, ticketId: number) =>
        `/api/v1/classes/${eventId}/tickets/${ticketId}/toggle-status`,
      reorder: (eventId: number) =>
        `/api/v1/classes/${eventId}/tickets/reorder`,
    },
  },
  events: {
    key: 'events',
    list: '/api/v1/events',
    details: (eventId: string) => `/api/v1/events/${eventId}`,
    add: '/api/v1/events',
    edit: (eventId: string) => `/api/v1/events/${eventId}`,
    delete: (eventId: string) => `/api/v1/events/${eventId}`,
    sessionsList: (eventId: string) => `/api/v1/events/${eventId}/sessions`,
    addSession: (eventId: string) => `/api/v1/events/${eventId}/sessions`,
    editSession: (eventId: string, sessionId: string) =>
      `/api/v1/events/${eventId}/sessions/${sessionId}`,
    deleteSession: (eventId: string, sessionId: string) =>
      `/api/v1/events/${eventId}/sessions/${sessionId}`,
    addRecurrent: (eventId: string) => `/api/v1/events/${eventId}/recurrences`,
    deleteRecurrent: (eventId: string, recurrentId: string) =>
      `/api/v1/events/${eventId}/recurrences/${recurrentId}`,
    addPricing: (eventId: string) => `/api/v1/events/${eventId}/price-rules`,
    deletePricing: (eventId: string, ruleId: string) =>
      `/api/v1/events/${eventId}/price-rules/${ruleId}`,
  },
  trips: {
    key: 'trips',
    list: '/api/v1/trips',
    details: (tripId: string) => `/api/v1/trips/${tripId}`,
    add: '/api/v1/trips',
    edit: (tripId: string) => `/api/v1/trips/${tripId}`,
    delete: (tripId: string) => `/api/v1/trips/${tripId}`,
    toggleStatus: (tripId: string) => `/api/v1/trips/${tripId}/toggle-status`,
    destinations: '/api/v1/trips/destinations/list',
    bookings: {
      list: (tripId: number) => `/api/v1/trips/${tripId}/bookings`,
      add: (tripId: number) => `/api/v1/trips/${tripId}/bookings`,
      markContacted: (tripId: number, bookingId: number) =>
        `/api/v1/trips/${tripId}/bookings/${bookingId}/mark-contacted`,
      cancel: (tripId: number, bookingId: number) =>
        `/api/v1/trips/${tripId}/bookings/${bookingId}/cancel`,
      update: (tripId: number, bookingId: number) =>
        `/api/v1/trips/${tripId}/bookings/${bookingId}`,
    },
    media: {
      delete: (tripId: number, mediaId: number) =>
        `/api/v1/trips/${tripId}/media/${mediaId}`,
      edit: (tripId: number) => `/api/v1/trips/${tripId}/media`,
    },
    tickets: {
      list: (tripId: number) => `/api/v1/trips/${tripId}/tickets`,
      add: (tripId: number) => `/api/v1/trips/${tripId}/tickets`,
      details: (tripId: number, ticketId: number) =>
        `/api/v1/trips/${tripId}/tickets/${ticketId}`,
      edit: (tripId: number, ticketId: number) =>
        `/api/v1/trips/${tripId}/tickets/${ticketId}`,
      delete: (tripId: number, ticketId: number) =>
        `/api/v1/trips/${tripId}/tickets/${ticketId}`,
      reorder: (tripId: number) => `/api/v1/trips/${tripId}/tickets/reorder`,
      toggleStatus: (tripId: number, ticketId: number) =>
        `/api/v1/trips/${tripId}/tickets/${ticketId}/toggle-status`,
    },
    customField: {
      add: (tripId: number) => `/api/v1/trips/${tripId}/custom-fields/upsert`,
      get: (tripId: number) => `/api/v1/trips/${tripId}/custom-fields`,
      reorder: (tripId: number) =>
        `/api/v1/trips/${tripId}/custom-fields/reorder`,
      delete: (tripId: number, customFieldId: number) =>
        `/api/v1/trips/${tripId}/custom-fields/${customFieldId}`,
    },
  },
  transactions: {
    list: '/api/v1/transactions',
    statistics: '/api/v1/transactions/statistics',
    export: '/api/v1/transactions/export',
    delete: (id: number) => `/api/v1/transactions/${id}`,
  },
  campaigns: {
    list: '/api/v1/campaigns',
    add: '/api/v1/campaigns',
    userCampaigns: (userId: number) => `/api/v1/users/${userId}/campaigns`,
  },
  admins: {
    key: 'admins',
    list: '/api/v1/admins',
    add: '/api/v1/admins',
    details: (adminId: string) => `/api/v1/admins/${adminId}`,
    edit: (adminId: string) => `/api/v1/admins/${adminId}`,
    delete: (adminId: string) => `/api/v1/admins/${adminId}`,
    toggleStatus: (adminId: string) =>
      `/api/v1/admins/${adminId}/toggle-status`,
  },
  roles: {
    key: 'roles',
    list: '/api/v1/roles',
    meta: '/api/v1/roles/metadata',
    add: '/api/v1/roles',
    details: (roleId: string) => `/api/v1/roles/${roleId}`,
    edit: (roleId: string) => `/api/v1/roles/${roleId}`,
    delete: (roleId: string) => `/api/v1/roles/${roleId}`,
  },
  customers: {
    key: 'tenant-users',
    list: '/api/v1/tenant-users',
    add: '/api/v1/tenant-users',
    search: '/api/v1/tenant-users/search',
    findByPhoneNumber: `/api/v1/users/find-one`,
    toggleStatus: (id: number) => `/api/v1/tenant-users/${id}/toggle-status`,
    details: (customerId: number) => `/api/v1/tenant-users/${customerId}`,
    export: '/api/v1/tenant-users/export',
  },
  tournaments: {
    key: 'tournaments',
    list: '/api/v1/tournaments',
    add: '/api/v1/tournaments',
    details: (tournamentId: number) => `/api/v1/tournaments/${tournamentId}`,
    edit: (tournamentId: number) => `/api/v1/tournaments/${tournamentId}`,
    delete: (tournamentId: number) => `/api/v1/tournaments/${tournamentId}`,
    publish: (tournamentId: number) =>
      `/api/v1/tournaments/${tournamentId}/publish`,
    // Tournament Contact Numbers
    contactNumbers: {
      list: (tournamentId: number) =>
        `/api/v1/tournaments/${tournamentId}/contact-numbers`,
      add: (tournamentId: number) =>
        `/api/v1/tournaments/${tournamentId}/contact-numbers`,
      update: (tournamentId: number, contactNumberId: number) =>
        `/api/v1/tournaments/${tournamentId}/contact-numbers/${contactNumberId}`,
      delete: (tournamentId: number, contactNumberId: number) =>
        `/api/v1/tournaments/${tournamentId}/contact-numbers/${contactNumberId}`,
    },
    // Tournament Divisions
    divisions: {
      list: (tournamentId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions`,
      add: (tournamentId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions`,
      details: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}`,
      update: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}`,
      delete: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}`,
      reorder: (tournamentId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/reorder`,
      groups: {
        list: (tournamentId: number, divisionId: number) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups`,
      },
      knockoutStages: {
        list: (tournamentId: number, divisionId: number) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/knockout-stages`,
        add: (tournamentId: number, divisionId: number) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/knockout-stages`,
        update: (
          tournamentId: number,
          divisionId: number,
          knockoutStageId: number,
        ) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/knockout-stages/${knockoutStageId}`,
        delete: (
          tournamentId: number,
          divisionId: number,
          knockoutStageId: number,
        ) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/knockout-stages/${knockoutStageId}`,
        reorder: (tournamentId: number, divisionId: number) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/knockout-stages/reorder`,
      },
      configuration: {
        list: (tournamentId: number, divisionId: number) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/configuration`,
        update: (tournamentId: number, divisionId: number) =>
          `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/configuration`,
      },
    },
    // Tournament Teams
    teams: {
      list: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams`,
      add: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams`,
      details: (tournamentId: number, divisionId: number, teamId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams/${teamId}`,
      approve: (tournamentId: number, divisionId: number, teamId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams/${teamId}/approve`,
      reject: (tournamentId: number, divisionId: number, teamId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams/${teamId}/reject`,
      update: (tournamentId: number, divisionId: number, teamId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams/${teamId}`,
      delete: (tournamentId: number, divisionId: number, teamId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams/${teamId}`,
      core: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/teams/core`,
    },
    // Tournament Division Groups
    groups: {
      add: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups`,
      list: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups`,
      update: (
        tournamentId: number | string,
        divisionId: number,
        groupId: number,
      ) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups/${groupId}`,
      delete: (tournamentId: number, divisionId: number, groupId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups/${groupId}`,
      generate: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups/generate`,
      reorder: (tournamentId: number, divisionId: number, groupId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups/${groupId}/teams/reorder`,
      updateTeamStandings: (
        tournamentId: number,
        divisionId: number,
        groupId: number,
        teamId: number,
      ) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/groups/${groupId}/teams/${teamId}/standings`,
    },
    matches: {
      list: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/matches`,
      add: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/matches`,
      update: (tournamentId: number, divisionId: number, matchId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/matches/${matchId}`,
      result: (tournamentId: number, divisionId: number, matchId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/matches/${matchId}/result`,
      delete: (tournamentId: number, divisionId: number, matchId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/matches/${matchId}`,
      generate: (tournamentId: number, divisionId: number) =>
        `/api/v1/tournaments/${tournamentId}/divisions/${divisionId}/matches/generate`,
    },
  },
  statistics: {
    all: `api/v1/statistics`,
  },
  customerGroups: {
    key: 'customer-groups',
    list: '/api/v1/customer-groups',
    add: '/api/v1/customer-groups',
    details: (id: number) => `/api/v1/customer-groups/${id}`,
    update: (id: number) => `/api/v1/customer-groups/${id}`,
    delete: (id: number) => `/api/v1/customer-groups/${id}`,
    importTemplate: '/api/v1/customer-groups/import-template',
    shortList: '/api/v1/customer-groups/list',
    members: {
      list: (groupId: number) => `/api/v1/customer-groups/${groupId}/members`,
      add: (groupId: number) => `/api/v1/customer-groups/${groupId}/members`,
      remove: (groupId: number, userId: number) =>
        `/api/v1/customer-groups/${groupId}/members/${userId}`,
    },
    imports: {
      enqueue: (groupId: number) => `/api/v1/customer-groups/${groupId}/import`,
      status: (groupId: number, jobId: string) =>
        `/api/v1/customer-groups/${groupId}/imports/${jobId}`,
      resultDownload: (groupId: number, jobId: string) =>
        `/api/v1/customer-groups/${groupId}/imports/${jobId}/result.xlsx`,
    },
  },
};
